import React, { useState, useContext, useEffect, useRef } from 'react';
import { AuthContext } from '@/App';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Progress } from '@/components/ui/progress';
import { 
  AlertTriangle, 
  Trash2, 
  RefreshCw, 
  CheckCircle, 
  Database,
  Play,
  FileText,
  Shield,
  Loader2,
  Server,
  Rocket
} from 'lucide-react';

const GoLiveAdmin = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState({});
  const [results, setResults] = useState({});
  const [confirmReset, setConfirmReset] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [resetStatus, setResetStatus] = useState(null);
  const syncPollRef = useRef(null);
  const resetPollRef = useRef(null);

  // Cancel running sync
  const cancelSync = async () => {
    try {
      await axios.post(`${API}/admin/diagnostics/cancel-sync`);
      toast.success('Sincronização cancelada');
      
      // Stop polling
      if (syncPollRef.current) {
        clearInterval(syncPollRef.current);
        syncPollRef.current = null;
      }
      if (resetPollRef.current) {
        clearInterval(resetPollRef.current);
        resetPollRef.current = null;
      }
      
      // Update local state
      setLoading({ sync: false, reset: false });
      setSyncStatus(prev => prev ? { ...prev, status: 'cancelled', message: 'Cancelado pelo utilizador' } : null);
      setResetStatus(prev => prev ? { ...prev, status: 'cancelled', message: 'Cancelado pelo utilizador' } : null);
    } catch (error) {
      toast.error('Erro ao cancelar: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Poll for reset status
  const pollResetStatus = async (resetId) => {
    try {
      const url = resetId 
        ? `${API}/admin/go-live-reset-status?reset_id=${resetId}`
        : `${API}/admin/go-live-reset-status`;
      const response = await axios.get(url);
      setResetStatus(response.data);
      
      // Stop polling if completed or error
      if (response.data.status === 'completed' || response.data.status === 'error') {
        if (resetPollRef.current) {
          clearInterval(resetPollRef.current);
          resetPollRef.current = null;
        }
        setLoading(prev => ({ ...prev, reset: false }));
        setConfirmReset(false);
        
        if (response.data.status === 'completed') {
          setResults(prev => ({ 
            ...prev, 
            reset: { 
              success: true, 
              data: response.data 
            } 
          }));
          toast.success('Limpeza concluída!');
        } else {
          setResults(prev => ({ 
            ...prev, 
            reset: { 
              success: false, 
              error: response.data.errors?.join(', ') || 'Erro na limpeza'
            } 
          }));
          toast.error('Erro na limpeza');
        }
      }
    } catch (error) {
      console.error('Error polling reset status:', error);
    }
  };

  // Poll for sync status
  const pollSyncStatus = async (syncId) => {
    try {
      const url = syncId 
        ? `${API}/sage/go-live-sync-status?sync_id=${syncId}`
        : `${API}/sage/go-live-sync-status`;
      const response = await axios.get(url);
      setSyncStatus(response.data);
      
      // Stop polling if completed or error
      if (response.data.status === 'completed' || response.data.status === 'error') {
        if (syncPollRef.current) {
          clearInterval(syncPollRef.current);
          syncPollRef.current = null;
        }
        setLoading(prev => ({ ...prev, sync: false }));
        
        if (response.data.status === 'completed') {
          setResults(prev => ({ 
            ...prev, 
            sync: { 
              success: true, 
              data: response.data 
            } 
          }));
          toast.success('Sincronização SAGE concluída!');
        } else {
          setResults(prev => ({ 
            ...prev, 
            sync: { 
              success: false, 
              error: response.data.errors?.join(', ') || 'Erro na sincronização'
            } 
          }));
          toast.error('Erro na sincronização');
        }
      }
    } catch (error) {
      console.error('Error polling sync status:', error);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (syncPollRef.current) {
        clearInterval(syncPollRef.current);
      }
      if (resetPollRef.current) {
        clearInterval(resetPollRef.current);
      }
    };
  }, []);

  // Check for existing running operations on mount
  useEffect(() => {
    const checkExisting = async () => {
      // Check for running reset
      try {
        const resetResponse = await axios.get(`${API}/admin/go-live-reset-status`);
        if (resetResponse.data.status === 'running') {
          setResetStatus(resetResponse.data);
          setLoading(prev => ({ ...prev, reset: true }));
          resetPollRef.current = setInterval(() => pollResetStatus(resetResponse.data.reset_id), 2000);
        }
      } catch (error) {
        // Ignore - no existing reset
      }
      
      // Check for running sync
      try {
        const syncResponse = await axios.get(`${API}/sage/go-live-sync-status`);
        if (syncResponse.data.status === 'running') {
          setSyncStatus(syncResponse.data);
          setLoading(prev => ({ ...prev, sync: true }));
          syncPollRef.current = setInterval(() => pollSyncStatus(syncResponse.data.sync_id), 3000);
        }
      } catch (error) {
        // Ignore - no existing sync
      }
    };
    checkExisting();
  }, []);

  // Check if user is admin
  if (user?.role !== 'admin') {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-12 text-center">
            <Shield className="h-16 w-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-red-700">Acesso Restrito</h2>
            <p className="text-red-600 mt-2">Esta página é apenas para administradores.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const executeStep = async (stepId, endpoint, method = 'POST') => {
    setLoading(prev => ({ ...prev, [stepId]: true }));
    
    try {
      const timeout = 60000; // 1 minute timeout for starting the job
      const response = method === 'GET' 
        ? await axios.get(`${API}${endpoint}`, { timeout })
        : await axios.post(`${API}${endpoint}`, {}, { timeout });
      
      // For reset, start polling
      if (stepId === 'reset' && response.data.reset_id) {
        setResetStatus({ status: 'running', message: 'A iniciar...', ...response.data });
        // Start polling every 2 seconds
        resetPollRef.current = setInterval(() => pollResetStatus(response.data.reset_id), 2000);
        toast.info('Limpeza iniciada. A monitorizar progresso...');
        return; // Don't set results yet, wait for polling
      }
      
      // For sync, start polling
      if (stepId === 'sync' && response.data.sync_id) {
        setSyncStatus({ status: 'running', message: 'A iniciar...', ...response.data });
        // Start polling every 3 seconds
        syncPollRef.current = setInterval(() => pollSyncStatus(response.data.sync_id), 3000);
        toast.info('Sincronização iniciada. A monitorizar progresso...');
        return; // Don't set results yet, wait for polling
      }
      
      setResults(prev => ({ ...prev, [stepId]: { success: true, data: response.data } }));
      toast.success(`Executado com sucesso!`);
      setConfirmReset(false);
    } catch (error) {
      setResults(prev => ({ 
        ...prev, 
        [stepId]: { 
          success: false, 
          error: error.response?.data?.detail || error.message 
        } 
      }));
      toast.error(`Erro: ${error.response?.data?.detail || error.message}`);
      setLoading(prev => ({ ...prev, [stepId]: false }));
    } finally {
      // Stop loading for validation step (non-background) - stepId is 'validation'
      if (stepId === 'validation') {
        setLoading(prev => ({ ...prev, [stepId]: false }));
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="go-live-admin-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1000px] mx-auto px-4 md:px-6 py-4 md:py-6">
          <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Rocket className="h-5 w-5 md:h-6 md:w-6 text-red-600" />
            Go Live
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Preparar a aplicação para produção - eliminar dados de teste e sincronizar com SAGE real
          </p>
        </div>
      </div>

      <div className="max-w-[1000px] mx-auto px-4 md:px-6 py-4 md:py-6 space-y-6">
        
        {/* Step 1: Full Reset */}
        <Card className="border-red-300 border-2">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-red-100">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <CardTitle className="text-xl">Passo 1: Limpar Base de Dados</CardTitle>
                <CardDescription>
                  Elimina TODOS os dados de teste. Preserva apenas utilizadores reais e configurações de categorias.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {!confirmReset ? (
              <Button
                variant="destructive"
                size="lg"
                onClick={() => setConfirmReset(true)}
                disabled={loading.reset}
                className="w-full sm:w-auto"
                data-testid="btn-reset-start"
              >
                {loading.reset ? (
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-5 w-5 mr-2" />
                )}
                {loading.reset ? 'A limpar...' : 'Iniciar Limpeza'}
              </Button>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-6 w-6 text-red-600 mt-0.5" />
                    <div>
                      <p className="font-semibold text-red-800">Tem a certeza?</p>
                      <p className="text-sm text-red-700 mt-1">
                        Esta ação irá eliminar TODOS os dados de teste incluindo artigos, fornecedores, 
                        encomendas, consumos, alertas e histórico. Esta ação é IRREVERSÍVEL.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="destructive"
                    size="lg"
                    onClick={() => executeStep('reset', '/admin/go-live-migration')}
                    disabled={loading.reset}
                    data-testid="btn-reset-confirm"
                  >
                    {loading.reset ? (
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    ) : (
                      <AlertTriangle className="h-5 w-5 mr-2" />
                    )}
                    CONFIRMAR LIMPEZA
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => setConfirmReset(false)}
                    disabled={loading.reset}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            )}
            
            {/* Progress indicator while resetting */}
            {loading.reset && resetStatus && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-5 w-5 text-amber-600 animate-spin" />
                    <span className="font-semibold text-amber-800">Limpeza em Progresso</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={cancelSync}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    data-testid="btn-cancel-reset"
                  >
                    Cancelar
                  </Button>
                </div>
                <p className="text-sm text-amber-700 mb-3">{resetStatus.message || 'A processar...'}</p>
                {resetStatus.total_collections > 0 && (
                  <div className="mb-3">
                    <div className="flex justify-between text-xs text-amber-600 mb-1">
                      <span>Coleções: {resetStatus.collections_processed || 0}/{resetStatus.total_collections}</span>
                      <span>{Math.round(((resetStatus.collections_processed || 0) / resetStatus.total_collections) * 100)}%</span>
                    </div>
                    <Progress value={((resetStatus.collections_processed || 0) / resetStatus.total_collections) * 100} className="h-2" />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-white p-2 rounded border">
                    <div className="text-slate-500 text-xs">Documentos Eliminados</div>
                    <div className="font-semibold text-lg">{resetStatus.total_deleted || 0}</div>
                  </div>
                  <div className="bg-white p-2 rounded border">
                    <div className="text-slate-500 text-xs">Coleção Atual</div>
                    <div className="font-semibold text-sm truncate">{resetStatus.current_collection || '-'}</div>
                  </div>
                </div>
                <p className="text-xs text-amber-600 mt-3">
                  A actualizar automaticamente a cada 2 segundos...
                </p>
              </div>
            )}
            
            {results.reset && !loading.reset && (
              <div className={`mt-4 p-4 rounded-lg ${results.reset.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                {results.reset.success ? (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="h-6 w-6 text-green-600" />
                      <span className="font-semibold text-green-800 text-lg">Limpeza Concluída!</span>
                    </div>
                    <div className="bg-white p-3 rounded border text-sm">
                      <p><strong>Coleções eliminadas:</strong> {results.reset.data.collections_deleted || results.reset.data.summary?.collections_deleted}</p>
                      <p><strong>Documentos eliminados:</strong> {results.reset.data.total_deleted || results.reset.data.summary?.documents_deleted}</p>
                      <p><strong>Utilizadores mantidos:</strong> {results.reset.data.users_remaining?.join(', ')}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                    <div>
                      <span className="font-semibold text-red-800">Erro</span>
                      <p className="text-red-700 text-sm mt-1">{results.reset.error}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 2: SAGE Sync */}
        <Card className={`${results.reset?.success ? '' : 'opacity-60'}`}>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-blue-100">
                <RefreshCw className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-xl">Passo 2: Sincronizar com SAGE</CardTitle>
                <CardDescription>
                  Carrega artigos, fornecedores e dados do SAGE de produção.
                </CardDescription>
              </div>
              {results.sync?.success && (
                <Badge className="bg-green-600 ml-auto">Concluído</Badge>
              )}
              {loading.sync && (
                <Badge className="bg-blue-600 ml-auto animate-pulse">Em progresso</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <Button
              size="lg"
              onClick={() => executeStep('sync', '/sage/sync-full-go-live')}
              disabled={loading.sync || !results.reset?.success}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
              data-testid="btn-sync"
            >
              {loading.sync ? (
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-5 w-5 mr-2" />
              )}
              {loading.sync ? 'A sincronizar...' : 'Sincronizar SAGE'}
            </Button>
            
            {/* Progress indicator while syncing */}
            {loading.sync && syncStatus && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                    <span className="font-semibold text-blue-800">Sincronização em Progresso</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={cancelSync}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    data-testid="btn-cancel-sync"
                  >
                    Cancelar
                  </Button>
                </div>
                <p className="text-sm text-blue-700 mb-3">{syncStatus.message || 'A processar...'}</p>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                  <div className="bg-white p-2 rounded border">
                    <div className="text-slate-500 text-xs">Fornecedores</div>
                    <div className="font-semibold text-lg">{syncStatus.suppliers_synced || 0}</div>
                  </div>
                  <div className="bg-white p-2 rounded border">
                    <div className="text-slate-500 text-xs">Artigos</div>
                    <div className="font-semibold text-lg">{syncStatus.products_synced || 0}</div>
                  </div>
                  <div className="bg-white p-2 rounded border">
                    <div className="text-slate-500 text-xs">Novos</div>
                    <div className="font-semibold text-lg text-green-600">{syncStatus.products_created || 0}</div>
                  </div>
                  <div className="bg-white p-2 rounded border">
                    <div className="text-slate-500 text-xs">Página</div>
                    <div className="font-semibold text-lg">{syncStatus.current_page || 0}</div>
                  </div>
                </div>
                <p className="text-xs text-blue-600 mt-3">
                  A actualizar automaticamente a cada 3 segundos...
                </p>
              </div>
            )}
            
            {results.sync?.success && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="font-semibold text-green-800">Sincronização Concluída!</span>
                </div>
                <p className="text-sm text-green-700">
                  Artigos: {results.sync.data.products_synced || 0} ({results.sync.data.products_created || 0} novos, {results.sync.data.products_updated || 0} actualizados) | 
                  Fornecedores: {results.sync.data.suppliers_synced || 0}
                </p>
                {results.sync.data.duration_seconds && (
                  <p className="text-xs text-green-600 mt-1">
                    Duração: {Math.round(results.sync.data.duration_seconds)} segundos
                  </p>
                )}
                {results.sync.data.errors?.length > 0 && (
                  <div className="mt-2 text-sm text-amber-700">
                    Avisos: {results.sync.data.errors.join(', ')}
                  </div>
                )}
              </div>
            )}
            
            {results.sync?.success === false && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700">{results.sync.error}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 3: Validation */}
        <Card className={`${results.sync?.success ? '' : 'opacity-60'}`}>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-emerald-100">
                <FileText className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <CardTitle className="text-xl">Passo 3: Validar</CardTitle>
                <CardDescription>
                  Verificar que os dados foram sincronizados correctamente.
                </CardDescription>
              </div>
              {results.validation?.success && (
                <Badge className="bg-green-600 ml-auto">Verificado</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <Button
              size="lg"
              onClick={() => executeStep('validation', '/admin/validation-report', 'GET')}
              disabled={loading.validation || !results.sync?.success}
              className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700"
              data-testid="btn-validate"
            >
              {loading.validation ? (
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              ) : (
                <FileText className="h-5 w-5 mr-2" />
              )}
              Gerar Relatório
            </Button>
            
            {results.validation?.success && (
              <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                  <span className="font-semibold text-emerald-800">Relatório de Validação</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                  {Object.entries(results.validation.data.counts || {}).map(([key, value]) => (
                    <div key={key} className="bg-white p-2 rounded border">
                      <p className="text-muted-foreground text-xs">{key}</p>
                      <p className="font-bold text-lg">{value}</p>
                    </div>
                  ))}
                </div>
                {results.validation.data.validations && (
                  <div className="mt-3 space-y-1">
                    {results.validation.data.validations.map((v, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        {v.status === 'ok' ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <AlertTriangle className="h-4 w-4 text-amber-600" />
                        )}
                        <span>{v.check}: {v.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Success Message */}
        {results.validation?.success && (
          <Card className="border-green-300 bg-green-50">
            <CardContent className="py-8 text-center">
              <CheckCircle className="h-16 w-16 mx-auto text-green-600 mb-4" />
              <h2 className="text-2xl font-bold text-green-800">Go Live Concluído!</h2>
              <p className="text-green-700 mt-2">
                A aplicação está pronta para utilização com dados reais.
              </p>
              <Button
                className="mt-4 bg-green-600 hover:bg-green-700"
                onClick={() => window.location.href = '/'}
              >
                Ir para o Dashboard
              </Button>
            </CardContent>
          </Card>
        )}

      </div>
    </div>
  );
};

export default GoLiveAdmin;
